import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";

import { numberWithCommas } from "modules/functions";
import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import PageTitle from "components/text/PageTitle";
import RoundActionIcon from "components/buttons/RoundActionIcon";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import ButtonPrimarySmall from "components/buttons/ButtonPrimarySmall";
import Tip from "components/other/Tip";
import ToggleSwitch from "components/forms/ToggleSwitch";
import Backdrop from "../other/Backdrop";
import ModalWindow from "../other/ModalWindow";
import InputWithLabel from "components/forms/InputWithLabel";
import InputText from "components/forms/InputText";
import ButtonPrimary from "components/buttons/ButtonPrimary";
import ButtonWhite from "../buttons/ButtonWhite";
import ButtonPrimaryDanger from "components/buttons/ButtonPrimaryDanger";

import returnIcon from "assets/img/icons/return-icon.svg";
import userCircleIcon from "assets/img/icons/user-single-icon.svg";
import mailPreviewWhiteIcon from "assets/img/icons/mail-preview-icon-white.svg";
import pencilIcon from "assets/img/icons/pencil-icon.svg";
import pencilWhiteIcon from "assets/img/icons/pencil-icon-white.svg";
import copyIcon from "assets/img/icons/copy-icon.svg";
import copyWhiteIcon from "assets/img/icons/copy-icon-white.svg";
import binIcon from "assets/img/icons/bin-icon.svg";
import binWhiteIcon from "assets/img/icons/bin-icon-white.svg";
import listIcon from "assets/img/icons/list-icon.svg";
import listWhiteIcon from "assets/img/icons/list-icon-white.svg";

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackIconWrap = styled.div``;

const AutoresponderDataBox = styled.div`
  position: relative;
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const TopColumns = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: column;
  }
`;

const LeftBoxColumn = styled.div`
  flex: 0.5;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const StatsBoxTitle = styled.div`
  font-weight: 500;
`;

const StatsBox = styled.div`
  padding: 15px 20px;
  width: 100%;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: grid;
  grid-template-columns: 37px 73px 73px 1fr;
  grid-template-rows: 25px 1fr 1fr;
`;

const BoxLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
`;

const LabelIconCell = styled.div`
  display: flex;
  align-items: center;
`;

const LabelIcon = styled.div`
  width: 16px;
  min-width: 16px;
  height: 16px;
`;

const ValueCell = styled.div`
  height: 24px;

  font-size: 13px;

  display: flex;
  align-items: center;
`;

const ChangeBadge = styled.div`
  height: 16px;
  padding: 0 4px 1px;
  margin-left: 5px;

  font-size: 11px;

  border-radius: 8px;
  background: ${(props) =>
    props.green ? css.POSITIVE_GREEN : css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const EditButtonWrap = styled.div`
  display: flex;
`;

const RightBoxColumn = styled.div`
  flex: 0.5;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const RightBoxColumnData = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const RightBoxColumnDataTitle = styled.div`
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const RightBoxColumnDataContent = styled.div``;

const ConnectedPagesList = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 6px;
`;

const ConnectedPage = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const ConnectedPageName = styled.div`
  line-height: 1;
`;

const ConnectedPageButtonWrap = styled.div``;

const NoPageConnected = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Badge = styled.div`
  height: 20px;
  padding: 0 14px 1px;

  font-size: 13px;
  color: #fff;
  white-space: nowrap;

  border-radius: 10px;
  background: ${css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const AddLandingPageButtonWrap = styled.div`
  margin-top: 6px;
`;

const StatusControls = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Data = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DataTitle = styled.div`
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const DataRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const StatusBadgeBox = styled.div``;

const StatusBadge = styled.div`
  height: 20px;
  padding: 0 14px 1px;

  font-size: 13px;
  color: ${(props) => (props.status === "paused" ? css.DARK_BLUE : "#fff")};
  white-space: nowrap;

  border-radius: 10px;
  background: ${(props) => {
    switch (props.status) {
      case "active":
        return css.BRAND_GREEN;
      case "paused":
        return css.BRAND_BLUSH;
      default:
        return css.TEXT_GREY;
    }
  }};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const ControlButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModalWrap = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1000;
`;

const EditAutoresponderModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);

  width: 100%;
  max-width: 500px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 200;

  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const EditAutoresponder = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonWrap = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_MEDIUM};

  display: flex;
`;

const Warning = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN};

  font-size: 13px;
  font-weight: 500;
  color: ${css.ERROR_RED};
`;

const ConfirmModalWrap = styled.div`
  margin-top: 60px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ConfirmModalText = styled.div`
  width: 100%;
  line-height: 130%;
`;

const ConfirmModalButtons = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_XLARGE};

  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${css.BUTTONS_GAP};
`;

export default ({
  data,
  onStatusToggleClick,
  loadingStatusToggle,
  onEditClick,
  editModal,
  name,
  handleInputChange,
  closeEditModal,
  onDuplicateClick,
  warnings,
  onEditAutoresponder,
  loadingEdit,
  editError,
  confirmDuplicate,
  closeDuplicateModal,
  onConfirmDuplicate,
  duplicateError,
  loadingDuplicate,
  onDeleteClick,
  onConfirmDelete,
  confirmDelete,
  deleteError,
  loadingDelete,
  closeDeleteModal,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const from = searchParams.get("from") ? searchParams.get("from") : null;

  const changeBadge = (change) => {
    if (typeof change !== "number" || change === 0) return <></>;

    if (change > 100) return <ChangeBadge green={true}>&gt;100%</ChangeBadge>;

    const roundCoef = change >= 10 || change <= -10 ? 1 : 10;
    let changeRounded = Math.round(change * roundCoef) / roundCoef;

    if (changeRounded >= 0)
      return <ChangeBadge green={true}>+{changeRounded}%</ChangeBadge>;
    else return <ChangeBadge green={false}>{changeRounded}%</ChangeBadge>;
  };

  const timelimit24Hours = new Date();
  timelimit24Hours.setDate(timelimit24Hours.getDate() - 1);

  const timelimit48Hours = new Date();
  timelimit48Hours.setDate(timelimit48Hours.getDate() - 2);

  const timelimit7Days = new Date();
  timelimit7Days.setDate(timelimit7Days.getDate() - 7);

  const timelimit14Days = new Date();
  timelimit14Days.setDate(timelimit14Days.getDate() - 14);

  const last24HoursLeads = data.responderData.leads.filter(
    (lead) => new Date(lead.created_at).getTime() > timelimit24Hours.getTime()
  );

  const prev24HoursLeads = data.responderData.leads.filter(
    (lead) =>
      new Date(lead.created_at).getTime() > timelimit48Hours.getTime() &&
      new Date(lead.created_at).getTime() <= timelimit24Hours.getTime()
  );

  const last24HoursLeadsChange =
    prev24HoursLeads.length > 0
      ? (100 / prev24HoursLeads.length) * last24HoursLeads.length - 100
      : "-";

  const last7DaysLeads = data.responderData.leads.filter(
    (lead) => new Date(lead.created_at).getTime() > timelimit7Days.getTime()
  );

  const prev7DaysLeads = data.responderData.leads.filter(
    (lead) =>
      new Date(lead.created_at).getTime() > timelimit14Days.getTime() &&
      new Date(lead.created_at).getTime() <= timelimit7Days.getTime()
  );

  const last7DaysLeadsChange =
    prev7DaysLeads.length > 0
      ? (100 / prev7DaysLeads.length) * last7DaysLeads.length - 100
      : "-";

  const last24HoursSent = data.responderData.sent_emails.filter(
    (email) => new Date(email.created_at).getTime() > timelimit24Hours.getTime()
  );

  const prev24HoursSent = data.responderData.sent_emails.filter(
    (email) =>
      new Date(email.created_at).getTime() > timelimit48Hours.getTime() &&
      new Date(email.created_at).getTime() <= timelimit24Hours.getTime()
  );

  const last24HoursSentChange =
    prev24HoursSent.length > 0
      ? (100 / prev24HoursSent.length) * last24HoursSent.length - 100
      : "-";

  const last7DaysSent = data.responderData.sent_emails.filter(
    (email) => new Date(email.created_at).getTime() > timelimit7Days.getTime()
  );

  const prev7DaysSent = data.responderData.sent_emails.filter(
    (email) =>
      new Date(email.created_at).getTime() > timelimit14Days.getTime() &&
      new Date(email.created_at).getTime() <= timelimit7Days.getTime()
  );

  const last7DaysSentChange =
    prev7DaysSent.length > 0
      ? (100 / prev7DaysSent.length) * last7DaysSent.length - 100
      : "-";

  // prepare Connected pages list
  const connectedPages = data.responderData.lists.map((list) => {
    return (
      <ConnectedPage>
        <ConnectedPageName>{list.name}</ConnectedPageName>
        <ConnectedPageButtonWrap>
          <ButtonRoundIcon
            icon={listIcon}
            hoverIcon={listWhiteIcon}
            background={css.BRAND_YELLOW}
            hoverBackground={css.BRAND_GREEN}
            onClick={() =>
              navigate(
                `/mailing-list?id=${list.id}&from=autoresponder&responderId=${data.responderData.id}`
              )
            }
            buttonSize="22px"
            iconSize="10px"
          />
        </ConnectedPageButtonWrap>
      </ConnectedPage>
    );
  });

  let returnTo = "/autoresponders";

  if (from === "mailing-list") {
    const listId = searchParams.get("listId")
      ? searchParams.get("listId")
      : null;

    if (listId) {
      returnTo = `/mailing-list?id=${listId}`;
    }
  }

  if (from === "lead-detail") {
    const leadId = searchParams.get("leadId")
      ? searchParams.get("leadId")
      : null;

    if (leadId) {
      returnTo = `/lead-detail?id=${leadId}`;
    }
  }

  const { status } = data.responderData;

  let statusText = "Active";

  if (status === "paused") statusText = "Paused";

  return (
    <>
      <Header>
        <PageTitle>{data.responderData.name}</PageTitle>
        <BackIconWrap>
          <RoundActionIcon
            icon={returnIcon}
            iconSize="16px"
            iconAlt="Return"
            onClick={() => navigate(returnTo)}
          />
        </BackIconWrap>
      </Header>
      <AutoresponderDataBox>
        <TopColumns>
          <LeftBoxColumn>
            <StatsBoxTitle>Autoresponder Stats</StatsBoxTitle>
            <StatsBox>
              <BoxLabel></BoxLabel>
              <BoxLabel>24 Hours</BoxLabel>
              <BoxLabel>7 Days</BoxLabel>
              <BoxLabel>Total</BoxLabel>
              <LabelIconCell>
                <LabelIcon>
                  <img src={userCircleIcon} style={{ width: "100%" }} />
                </LabelIcon>
              </LabelIconCell>
              <ValueCell>
                {numberWithCommas(last24HoursLeads.length)}
                {changeBadge(last24HoursLeadsChange)}
              </ValueCell>
              <ValueCell>
                {numberWithCommas(last7DaysLeads.length)}
                {changeBadge(last7DaysLeadsChange)}
              </ValueCell>
              <ValueCell>
                {numberWithCommas(
                  data.responderData.leads_aggregate.aggregate.count
                )}
              </ValueCell>
              <LabelIconCell>
                <LabelIcon>
                  <img src={mailPreviewWhiteIcon} style={{ width: "100%" }} />
                </LabelIcon>
              </LabelIconCell>
              <ValueCell>
                {numberWithCommas(last24HoursSent.length)}
                {changeBadge(last24HoursSentChange)}
              </ValueCell>
              <ValueCell>
                {numberWithCommas(last7DaysSent.length)}
                {changeBadge(last7DaysSentChange)}
              </ValueCell>
              <ValueCell>
                {numberWithCommas(
                  data.responderData.sent_emails_aggregate.aggregate.count
                )}
              </ValueCell>
            </StatsBox>
          </LeftBoxColumn>
          <RightBoxColumn>
            <RightBoxColumnData>
              <RightBoxColumnDataTitle>
                Connected Mailing Lists
              </RightBoxColumnDataTitle>
              <RightBoxColumnDataContent>
                <ConnectedPagesList>
                  {connectedPages.length > 0 && connectedPages}
                  {connectedPages.length === 0 && (
                    <NoPageConnected>
                      <Badge>No list connected</Badge>
                      <Tip
                        tipId="noList"
                        tip="No mailing list has this autoresponder set up."
                        position="bottom"
                      />
                    </NoPageConnected>
                  )}
                  <AddLandingPageButtonWrap>
                    <ButtonPrimarySmall
                      onClick={() =>
                        navigate(
                          `/mailing-lists?from=autoresponder&responder-id=${data.responderData.id}`
                        )
                      }
                    >
                      Mailing Lists
                    </ButtonPrimarySmall>
                  </AddLandingPageButtonWrap>
                </ConnectedPagesList>
              </RightBoxColumnDataContent>
            </RightBoxColumnData>
          </RightBoxColumn>
        </TopColumns>
        <StatusControls>
          <Data>
            <DataTitle>Status</DataTitle>
            <DataRow>
              <ToggleSwitch
                on={status === "active"}
                onClick={onStatusToggleClick}
                loading={loadingStatusToggle}
              />
              <StatusBadgeBox>
                <StatusBadge status={status}>{statusText}</StatusBadge>
              </StatusBadgeBox>
            </DataRow>
          </Data>
          <ControlButtons>
            <ButtonRoundIcon
              icon={pencilIcon}
              hoverIcon={pencilWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              onClick={onEditClick}
              buttonSize="34px"
              iconSize="15px"
            />
            <ButtonRoundIcon
              icon={copyIcon}
              hoverIcon={copyWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              onClick={onDuplicateClick}
              buttonSize="34px"
              iconSize="15px"
            />
            <ButtonRoundIcon
              icon={binIcon}
              hoverIcon={binWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.NEGATIVE_RED}
              onClick={onDeleteClick}
              buttonSize="34px"
              iconSize="15px"
              disabled={loadingDelete}
            />
          </ControlButtons>
        </StatusControls>
      </AutoresponderDataBox>
      {editModal && (
        <ModalWrap>
          <Backdrop onClick={closeEditModal} background="rgba(0, 0, 0, 0.7)" />
          <EditAutoresponderModal>
            <ModalWindow
              title="Edit Autoresponder"
              handleModalClose={closeEditModal}
              fullScreenHeight={true}
            >
              <EditAutoresponder>
                <InputWithLabel label="Name" width="100%" marginTop="50px">
                  <InputText
                    type="text"
                    name="name"
                    value={name}
                    placeholder="Enter the autoresponder name"
                    onChange={handleInputChange}
                    maxLength={40}
                    warning={warnings.includes("name")}
                  />
                </InputWithLabel>
                <ButtonWrap>
                  <ButtonPrimary
                    onClick={onEditAutoresponder}
                    disabled={loadingEdit}
                  >
                    Save
                  </ButtonPrimary>
                </ButtonWrap>
                {editError && <Warning>{editError}</Warning>}
              </EditAutoresponder>
            </ModalWindow>
          </EditAutoresponderModal>
        </ModalWrap>
      )}
      {confirmDuplicate && (
        <ModalWrap>
          <Backdrop
            onClick={closeDuplicateModal}
            background="rgba(0, 0, 0, 0.7)"
          />
          <EditAutoresponderModal>
            <ModalWindow
              title="Duplicate Autoresponder"
              handleModalClose={closeDuplicateModal}
              fullScreenHeight={true}
            >
              <ConfirmModalWrap>
                <ConfirmModalText>
                  A copy of the autoresponder, including email messages, will be
                  created.
                </ConfirmModalText>
                <ConfirmModalButtons>
                  <ButtonWhite onClick={closeDuplicateModal}>
                    Cancel
                  </ButtonWhite>
                  <ButtonPrimary
                    onClick={onConfirmDuplicate}
                    disabled={loadingDuplicate}
                  >
                    Proceed
                  </ButtonPrimary>
                </ConfirmModalButtons>
                {duplicateError && <Warning>{duplicateError}</Warning>}
              </ConfirmModalWrap>
            </ModalWindow>
          </EditAutoresponderModal>
        </ModalWrap>
      )}
      {confirmDelete && (
        <ModalWrap>
          <Backdrop
            onClick={closeDeleteModal}
            background="rgba(0, 0, 0, 0.7)"
          />
          <EditAutoresponderModal>
            <ModalWindow
              title="Delete Autoresponder"
              handleModalClose={closeDeleteModal}
              fullScreenHeight={true}
            >
              <ConfirmModalWrap>
                <ConfirmModalText>
                  Are you sure you want to delete the autoresponder and all the
                  email messages it contains?
                </ConfirmModalText>
                <ConfirmModalButtons>
                  <ButtonWhite onClick={closeDeleteModal}>Cancel</ButtonWhite>
                  <ButtonPrimaryDanger
                    onClick={onConfirmDelete}
                    disabled={loadingDelete}
                  >
                    Delete
                  </ButtonPrimaryDanger>
                </ConfirmModalButtons>
                {deleteError && <Warning>{deleteError}</Warning>}
              </ConfirmModalWrap>
            </ModalWindow>
          </EditAutoresponderModal>
        </ModalWrap>
      )}
    </>
  );
};
